import type {
  LoanAgencyFields,
  LoanApplication,
  LoanApplicationDocument,
  LoanAssetLine,
  LoanBorrower,
  LoanChecklistItem,
  LoanEmploymentRow,
  LoanFlowProfile,
  LoanLiabilityLine,
  LoanProductType,
  LoanPropertySnapshot,
  LoanTrackRecordRow,
} from '@/types/LoanApplicationType';
import { rtkApi } from '../rtkApi';

type ApiWrap<T> = { success: boolean; data: T };

export const loanApplicationApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    getMyLoanApplications: builder.query<LoanApplication[], void>({
      query: () => ({ url: '/loan-applications', method: 'GET' }),
      transformResponse: (r: ApiWrap<LoanApplication[]>) => r.data ?? [],
      providesTags: [{ type: 'LoanApplication' as const, id: 'LIST' }],
    }),
    getLoanApplication: builder.query<LoanApplication, string>({
      query: (id) => ({ url: `/loan-applications/${id}`, method: 'GET' }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      providesTags: (_res, _e, id) => [
        { type: 'LoanApplication' as const, id },
      ],
    }),
    createLoanApplication: builder.mutation<
      LoanApplication,
      {
        productType?: LoanProductType;
        flowProfile?: LoanFlowProfile;
        sourceProductId?: string;
        approvalChannel?: string;
        investorModeAtCreation?: boolean;
      }
    >({
      query: (body) => ({
        url: '/loan-applications',
        method: 'POST',
        data: body,
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: (result) =>
        result?._id
          ? [
              { type: 'LoanApplication', id: 'LIST' },
              { type: 'LoanApplication', id: result._id },
            ]
          : [{ type: 'LoanApplication', id: 'LIST' }],
    }),
    getOrCreatePurchaseLoanApplication: builder.mutation<
      LoanApplication,
      {
        flowProfile: LoanFlowProfile;
        focusProductId?: string;
        sourceProductId?: string;
        productType?: LoanProductType;
        approvalChannel?: string;
        investorModeAtCreation?: boolean;
      }
    >({
      query: (body) => ({
        url: '/loan-applications/for-me',
        method: 'POST',
        data: body,
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: (result) =>
        result?._id
          ? [
              { type: 'LoanApplication', id: 'LIST' },
              { type: 'LoanApplication', id: result._id },
              { type: 'LoanApplication', id: 'ADMIN_LIST' },
              { type: 'LoanApplication', id: 'ADMIN_SUMMARY' },
            ]
          : [
              { type: 'LoanApplication', id: 'LIST' },
              { type: 'LoanApplication', id: 'ADMIN_LIST' },
              { type: 'LoanApplication', id: 'ADMIN_SUMMARY' },
            ],
    }),
    patchLoanFocusProduct: builder.mutation<
      LoanApplication,
      { id: string; focusProductId: string; productType?: LoanProductType }
    >({
      query: ({ id, focusProductId, productType }) => ({
        url: `/loan-applications/${id}/focus-product`,
        method: 'PATCH',
        data: { focusProductId, ...(productType ? { productType } : {}) },
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'LoanApplication', id },
        { type: 'LoanApplication', id: 'LIST' },
        { type: 'LoanApplication', id: 'ADMIN_LIST' },
        { type: 'LoanApplication', id: 'ADMIN_SUMMARY' },
      ],
    }),
    patchLoanProperty: builder.mutation<
      LoanApplication,
      { id: string; property: Partial<LoanPropertySnapshot> }
    >({
      query: ({ id, property }) => ({
        url: `/loan-applications/${id}/property`,
        method: 'PATCH',
        data: property,
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'LoanApplication', id },
        { type: 'LoanApplication', id: 'LIST' },
      ],
    }),
    patchLoanBorrowers: builder.mutation<
      LoanApplication,
      { id: string; borrowers: LoanBorrower[] }
    >({
      query: ({ id, borrowers }) => ({
        url: `/loan-applications/${id}/borrowers`,
        method: 'PATCH',
        data: { borrowers },
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      // Shared profile sync can fill sibling apps — refresh all cached files.
      invalidatesTags: ['LoanApplication'],
    }),
    patchLoanAssets: builder.mutation<
      LoanApplication,
      { id: string; assetLines: LoanAssetLine[] }
    >({
      query: ({ id, assetLines }) => ({
        url: `/loan-applications/${id}/assets`,
        method: 'PATCH',
        data: { assetLines },
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: ['LoanApplication'],
    }),
    patchLoanTrackRecord: builder.mutation<
      LoanApplication,
      { id: string; trackRecord: LoanTrackRecordRow[] }
    >({
      query: ({ id, trackRecord }) => ({
        url: `/loan-applications/${id}/track-record`,
        method: 'PATCH',
        data: { trackRecord },
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'LoanApplication', id },
        { type: 'LoanApplication', id: 'LIST' },
      ],
    }),
    patchLoanEmployment: builder.mutation<
      LoanApplication,
      { id: string; employment: LoanEmploymentRow[] }
    >({
      query: ({ id, employment }) => ({
        url: `/loan-applications/${id}/employment`,
        method: 'PATCH',
        data: { employment },
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: ['LoanApplication'],
    }),
    patchLoanDeclarations: builder.mutation<
      LoanApplication,
      { id: string; declarations: Record<string, unknown> }
    >({
      query: ({ id, declarations }) => ({
        url: `/loan-applications/${id}/declarations`,
        method: 'PATCH',
        data: declarations,
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: ['LoanApplication'],
    }),
    patchLoanDscr: builder.mutation<
      LoanApplication,
      { id: string; dscrFields: Record<string, unknown> }
    >({
      query: ({ id, dscrFields }) => ({
        url: `/loan-applications/${id}/dscr`,
        method: 'PATCH',
        data: dscrFields,
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'LoanApplication', id },
        { type: 'LoanApplication', id: 'LIST' },
      ],
    }),
    patchLoanProductType: builder.mutation<
      LoanApplication,
      { id: string; productType: LoanProductType }
    >({
      query: ({ id, productType }) => ({
        url: `/loan-applications/${id}/product-type`,
        method: 'PATCH',
        data: { productType },
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'LoanApplication', id },
        { type: 'LoanApplication', id: 'LIST' },
        { type: 'LoanApplication', id: 'ADMIN_LIST' },
        { type: 'LoanApplication', id: 'ADMIN_SUMMARY' },
      ],
    }),
    patchLoanAgencyFields: builder.mutation<
      LoanApplication,
      { id: string } & Partial<LoanAgencyFields>
    >({
      query: ({ id, ...body }) => ({
        url: `/loan-applications/${id}/agency-fields`,
        method: 'PATCH',
        data: body,
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'LoanApplication', id },
        { type: 'LoanApplication', id: 'LIST' },
      ],
    }),
    patchLoanLiabilityLines: builder.mutation<
      LoanApplication,
      { id: string; liabilityLines: LoanLiabilityLine[] }
    >({
      query: ({ id, liabilityLines }) => ({
        url: `/loan-applications/${id}/liabilities`,
        method: 'PATCH',
        data: { liabilityLines },
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: ['LoanApplication'],
    }),
    patchLoanDemographics: builder.mutation<
      LoanApplication,
      { id: string } & Record<string, unknown>
    >({
      query: ({ id, ...body }) => ({
        url: `/loan-applications/${id}/demographics`,
        method: 'PATCH',
        data: body,
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: ['LoanApplication'],
    }),
    patchLoanChecklist: builder.mutation<
      LoanApplication,
      { id: string; checklist: LoanChecklistItem[] }
    >({
      query: ({ id, checklist }) => ({
        url: `/loan-applications/${id}/checklist`,
        method: 'PATCH',
        data: { checklist },
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'LoanApplication', id },
        { type: 'LoanApplication', id: 'LIST' },
        'User',
      ],
    }),
    submitLoanApplication: builder.mutation<LoanApplication, string>({
      query: (id) => ({
        url: `/loan-applications/${id}/submit`,
        method: 'POST',
        data: {},
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: (_r, _e, id) => [
        { type: 'LoanApplication', id },
        { type: 'LoanApplication', id: 'LIST' },
        { type: 'LoanApplication', id: 'ADMIN_LIST' },
        { type: 'LoanApplication', id: 'ADMIN_SUMMARY' },
        'User',
      ],
    }),
    withdrawLoanApplication: builder.mutation<LoanApplication, string>({
      query: (id) => ({
        url: `/loan-applications/${id}/withdraw`,
        method: 'POST',
        data: {},
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: (_r, _e, id) => [
        { type: 'LoanApplication', id },
        { type: 'LoanApplication', id: 'LIST' },
      ],
    }),
    signLoanAttestation: builder.mutation<
      LoanApplication,
      { id: string; acceptedTermsVersion?: string }
    >({
      query: ({ id, acceptedTermsVersion }) => ({
        url: `/loan-applications/${id}/sign`,
        method: 'POST',
        data: { acceptedTermsVersion },
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'LoanApplication', id },
        { type: 'LoanApplication', id: 'LIST' },
      ],
    }),
    getLoanApplicationDocuments: builder.query<
      LoanApplicationDocument[],
      string
    >({
      query: (applicationId) => ({
        url: `/loan-applications/${applicationId}/documents`,
        method: 'GET',
      }),
      transformResponse: (r: ApiWrap<LoanApplicationDocument[]>) =>
        r.data ?? [],
      providesTags: (_r, _e, applicationId) => [
        { type: 'LoanApplicationDocuments' as const, id: applicationId },
      ],
    }),
    uploadLoanApplicationDocument: builder.mutation<
      LoanApplicationDocument,
      {
        applicationId: string;
        file: File;
        checklistItemId?: string;
        title?: string;
      }
    >({
      query: ({ applicationId, file, checklistItemId, title }) => {
        const formData = new FormData();
        formData.append('file', file);
        if (checklistItemId)
          formData.append('checklistItemId', checklistItemId);
        if (title) formData.append('title', title);
        return {
          url: `/loan-applications/${applicationId}/documents/upload`,
          method: 'POST',
          data: formData,
        };
      },
      transformResponse: (r: ApiWrap<LoanApplicationDocument>) => r.data,
      invalidatesTags: (_r, _e, { applicationId }) => [
        { type: 'LoanApplication', id: applicationId },
        'DocumentRequirements',
        {
          type: 'LoanApplicationDocuments' as const,
          id: applicationId,
        },
        'User',
      ],
    }),
    adminLoanApplicationSummary: builder.query<
      { _id: Record<string, string>; count: number }[],
      void
    >({
      query: () => ({
        url: '/admin/loan-applications/summary',
        method: 'GET',
      }),
      transformResponse: (
        r: ApiWrap<{ _id: Record<string, string>; count: number }[]>
      ) => r.data ?? [],
      providesTags: [{ type: 'LoanApplication' as const, id: 'ADMIN_SUMMARY' }],
    }),
    adminListLoanApplications: builder.query<
      LoanApplication[],
      {
        status?: string;
        productType?: string;
        lifecycleStage?: string;
        limit?: number;
      } | void
    >({
      query: (params) => ({
        url: '/admin/loan-applications',
        method: 'GET',
        params: params || {},
      }),
      transformResponse: (r: ApiWrap<LoanApplication[]>) => r.data ?? [],
      providesTags: [{ type: 'LoanApplication', id: 'ADMIN_LIST' }],
    }),
    adminPatchLoanApplication: builder.mutation<
      LoanApplication,
      {
        id: string;
        status?: string;
        assignedToAdminId?: string | null;
        internalNotes?: string;
        checklist?: LoanChecklistItem[];
        productType?: LoanProductType;
        borrowerEditsUnlocked?: boolean;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/loan-applications/${id}`,
        method: 'PATCH',
        data: body,
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'LoanApplication', id },
        { type: 'LoanApplication', id: 'ADMIN_LIST' },
        { type: 'LoanApplication', id: 'ADMIN_SUMMARY' },
        { type: 'LoanApplication', id: 'LIST' },
      ],
    }),
    adminSeedLoanChecklist: builder.mutation<LoanApplication, string>({
      query: (id) => ({
        url: `/admin/loan-applications/${id}/seed-checklist`,
        method: 'POST',
        data: {},
      }),
      transformResponse: (r: ApiWrap<LoanApplication>) => r.data,
      invalidatesTags: (_r, _e, id) => [
        { type: 'LoanApplication', id },
        { type: 'LoanApplication', id: 'ADMIN_LIST' },
        { type: 'LoanApplication', id: 'ADMIN_SUMMARY' },
      ],
    }),
  }),
});

export const {
  useGetMyLoanApplicationsQuery,
  useGetLoanApplicationQuery,
  useCreateLoanApplicationMutation,
  useGetOrCreatePurchaseLoanApplicationMutation,
  usePatchLoanFocusProductMutation,
  usePatchLoanPropertyMutation,
  usePatchLoanBorrowersMutation,
  usePatchLoanAssetsMutation,
  usePatchLoanTrackRecordMutation,
  usePatchLoanEmploymentMutation,
  usePatchLoanDeclarationsMutation,
  usePatchLoanDscrMutation,
  usePatchLoanProductTypeMutation,
  usePatchLoanAgencyFieldsMutation,
  usePatchLoanLiabilityLinesMutation,
  usePatchLoanDemographicsMutation,
  usePatchLoanChecklistMutation,
  useSubmitLoanApplicationMutation,
  useWithdrawLoanApplicationMutation,
  useSignLoanAttestationMutation,
  useGetLoanApplicationDocumentsQuery,
  useUploadLoanApplicationDocumentMutation,
  useAdminListLoanApplicationsQuery,
  useAdminLoanApplicationSummaryQuery,
  useAdminPatchLoanApplicationMutation,
  useAdminSeedLoanChecklistMutation,
} = loanApplicationApi;
